import { useState } from 'react';

import { chatApi } from '../../api/chat';
import type { ConversationView } from '../../api/chat.types';
import { ErrorState, LoadingState } from '../../components/ScreenState';
import { Button, Checkbox, Input, Switch } from '../../ds';
import { useAsync } from '../../hooks/useAsync';
import { Avatar } from '../social/Avatar';
import { Chip } from '../social/Chip';
import { useSocial } from '../social/SocialContext';
import { SocialHeader } from '../social/SocialHeader';
import { SocialIconButton } from '../social/SocialIcon';

const FOCUSES = ['Kana', 'Từ vựng', 'Ngữ pháp', 'Luyện nghe', 'JLPT N5'];
const GOALS = [50, 100, 150, 200];
/// Khớp GROUP_NAME_MAX ở apps/api/src/chat/chat.dto.ts.
const GROUP_NAME_MAX = 40;
const label = { fontSize: 13, fontWeight: 600, color: 'var(--ink)' } as const;

/** SCR-CHAT-03 trong modal. */
export function CreateGroupPanel({ onClose, onCreated }: { onClose: () => void; onCreated: (group: ConversationView) => void }) {
  const { toast } = useSocial();
  const [retryKey, setRetryKey] = useState(0);
  const contacts = useAsync(() => chatApi.getContacts(), [retryKey]);
  const [name, setName] = useState('');
  const [focus, setFocus] = useState('Từ vựng');
  const [goal, setGoal] = useState(100);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [isPublic, setIsPublic] = useState(true);
  const [creating, setCreating] = useState(false);

  const count = picked.size;
  const canCreate = name.trim().length > 0 && count > 0 && !creating;

  const toggle = (id: string) =>
    setPicked((current) => {
      const next = new Set(current);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  const create = async () => {
    if (!canCreate) return;
    setCreating(true);
    try {
      onCreated(await chatApi.createGroup({ name: name.trim(), focus, weeklyGoal: goal, isPublic, memberIds: [...picked] }));
    } catch {
      toast('Chưa tạo được nhóm, thử lại sau');
      setCreating(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'min(760px, 88vh)', maxHeight: '100%', background: 'var(--surface-page)' }}>
      <SocialHeader title="Tạo nhóm học" right={<SocialIconButton name="close" label="Đóng" onClick={onClose} />} />
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 20 }}>
        <Input label="Tên nhóm" placeholder="Ví dụ: N5 cùng tiến" value={name} onChange={(e) => setName(e.target.value.slice(0, GROUP_NAME_MAX))} />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={label}>Nhóm tập trung vào</span>
          <div style={{ display: 'flex', columnGap: 8, flexWrap: 'wrap' }}>
            {FOCUSES.map((f) => (
              <Chip key={f} label={f} active={focus === f} onClick={() => setFocus(f)} />
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={label}>Mục tiêu chung mỗi tuần</span>
          <div style={{ display: 'flex', columnGap: 8, flexWrap: 'wrap' }}>
            {GOALS.map((g) => (
              <Chip key={g} label={`${g} thẻ SRS`} active={goal === g} onClick={() => setGoal(g)} />
            ))}
          </div>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Cộng dồn số thẻ cả nhóm ôn. Không đạt cũng không sao — chỉ để cùng nhìn tiến độ.</span>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={label}>{`Mời thành viên · đã chọn ${count}`}</span>
          {contacts.loading ? (
            <LoadingState />
          ) : contacts.error ? (
            <ErrorState message="Không tải được danh sách bạn học" onRetry={() => setRetryKey((k) => k + 1)} />
          ) : contacts.data?.length ? (
            contacts.data.map((c) => (
              // Cả hàng là vùng bấm; Checkbox chỉ để hiển thị nên không gắn onChange (click nổi lên hàng).
              <button
                key={c.id}
                type="button"
                role="checkbox"
                aria-checked={picked.has(c.id)}
                aria-label={c.name}
                className="sk-press"
                onClick={() => toggle(c.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  minHeight: 56,
                  padding: '0 8px',
                  margin: '0 -8px',
                  border: 'none',
                  borderRadius: 'var(--radius-control)',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontFamily: 'var(--font-vi)',
                }}
              >
                <Avatar name={c.name} size={40} />
                <span style={{ flex: 1 }}>
                  <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>{c.name}</span>
                  <span style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)' }}>{c.level}</span>
                </span>
                <span style={{ pointerEvents: 'none' }}>
                  <Checkbox checked={picked.has(c.id)} />
                </span>
              </button>
            ))
          ) : (
            <span style={{ fontSize: 14, color: 'var(--text-secondary)', padding: '12px 0' }}>Chưa có bạn học nào để mời. Hãy trò chuyện ở Cộng đồng trước nhé.</span>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 14, borderRadius: 'var(--radius-card)', background: 'var(--surface-tint)', border: '1px solid var(--border-default)' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)' }}>Nhóm công khai</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Hiện trong Cộng đồng để người cùng tiến độ xin vào</div>
          </div>
          <Switch checked={isPublic} onChange={setIsPublic} />
        </div>
      </div>
      <div style={{ padding: '12px 16px 16px', borderTop: '1px solid var(--border-default)', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
        <Button size="lg" disabled={!canCreate} style={{ justifyContent: 'center' }} onClick={create}>
          {count ? `Tạo nhóm · ${count + 1} người` : 'Tạo nhóm'}
        </Button>
      </div>
    </div>
  );
}
